import React from "react";
import {
Dialog,
DialogContent,
Box,
Typography,
Tabs,
Tab,
Divider,
Button,
Avatar,
IconButton,
Switch,
Select,
MenuItem,
FormControl
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useUser } from "./UserContext";
import { updateProfile } from "../api/auth";

// Dark theme colors
const DARK_COLORS = {
  PRIMARY: "#000000",
  SECONDARY: "#1a1a1a",
  ACCENT: "#2d2d2d",
  SURFACE: "#121212",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  BORDER: "#333333",
  HOVER: "#404040",
  SUCCESS: "#4caf50",
  ERROR: "#ff4444",
  WARNING: "#ff9800",
  INFO: "#2196f3"
};

const TRANSLATIONS: Record<string, any> = {
  Italian: {
    general: "Generale",
    profile: "Profilo",
    about: "Info",
    generalSettings: "⚙️ Impostazioni Generali",
    enableNotifications: "Abilita notifiche",
    allowNotifications: "Consenti notifiche email o push",
    darkMode: "Modalità scura",
    useDarkTheme: "Usa tema scuro",
    language: "Lingua",
    selectLanguage: "Seleziona lingua interfaccia",
    changePassword: "🔒 Cambia password",
    logout: "🚪 Esci",
    deleteAccount: "🗑️ Elimina account",
    version: "Versione:",
    contactSupport: "Per supporto, contatta:",
    email: "Email"
  },
  French: {
    general: "Général",
    profile: "Profil",
    about: "À propos",
    generalSettings: "⚙️ Paramètres généraux",
    enableNotifications: "Activer les notifications",
    allowNotifications: "Autoriser les notifications par e-mail ou push",
    darkMode: "Mode sombre",
    useDarkTheme: "Utiliser le thème sombre",
    language: "Langue",
    selectLanguage: "Sélectionner la langue de l'interface",
    changePassword: "🔒 Changer le mot de passe",
    logout: "🚪 Se déconnecter",
    deleteAccount: "🗑️ Supprimer le compte",
    version: "Version :",
    contactSupport: "Pour le support, contactez :",
    email: "E-mail"
  }
};

interface SettingsModalProps {
open: boolean;
onClose: () => void;
fullName?: string;
username?: string;
email?: string;
onLogout: () => void;
onOpenChangePw: () => void;
}

export default function SettingsModal({
open,
onClose,
fullName,
username,
email,
onLogout,
  onOpenChangePw
}: SettingsModalProps) {
  const [tab, setTab] = React.useState(1);
  const { language, setLanguage } = useUser();
  const t = TRANSLATIONS[language] || TRANSLATIONS["Italian"];
  const initials = (fullName || username || "U").charAt(0).toUpperCase();

  const REVERSE_LANGUAGE_MAPPING: Record<string, string> = {
    "Italian": "IT",
    "French": "FR",
    "English": "EN"
  };

  const handleLanguageChange = async (newLanguage: string) => {
    setLanguage(newLanguage);
    const apiLang = REVERSE_LANGUAGE_MAPPING[newLanguage];
    if (apiLang) {
      try {
        await updateProfile({ language: apiLang });
      } catch (error) {
        // Failed to update language preference
      }
    }
  };

  const SETTINGS_TABS = [
   { label: t.general },
   { label: t.profile },
   { label: t.about }
  ];

  return (
<Dialog
open={open}
onClose={onClose}
maxWidth="xs"
fullWidth
PaperProps={{
sx: {
borderRadius: 4,
zIndex: 1300,
position: "relative",
p: 0,
boxShadow: "0px 12px 48px rgba(0, 0, 0, 0.8)",
minWidth: 370,
maxWidth: 420,
backgroundColor: DARK_COLORS.SURFACE,
border: `1px solid ${DARK_COLORS.BORDER}`,
 }
 }}
>
<Box sx={{ 
  position: "relative", 
  pt: 3, 
  pb: 2, 
  px: 0, 
  backgroundColor: DARK_COLORS.SURFACE,
  borderRadius: 4,
}}>
<IconButton
onClick={onClose}
sx={{
position: "absolute",
right: 16,
top: 12,
color: DARK_COLORS.TEXT_SECONDARY,
"&:hover": {
backgroundColor: DARK_COLORS.HOVER,
color: DARK_COLORS.TEXT_PRIMARY,
transform: "scale(1.1)",
 },
cursor: "pointer",
zIndex: 2000,
transition: "all 0.2s ease",
 }}
>
<CloseIcon />
</IconButton>

<Tabs
value={tab}
onChange={(_, v) => setTab(v)}
centered
sx={{
mb: 2,
".MuiTabs-indicator": {
background: DARK_COLORS.TEXT_PRIMARY,
height: 3,
borderRadius: 2,
boxShadow: `0 0 8px rgba(255, 255, 255, 0.3)`,
 },
".MuiTab-root": {
fontWeight: 500,
color: DARK_COLORS.TEXT_SECONDARY,
fontSize: 15,
minHeight: 36,
textTransform: "none",
transition: "all 0.2s ease",
"&.Mui-selected": {
color: DARK_COLORS.TEXT_PRIMARY,
fontWeight: 600,
 },
"&:hover": {
color: DARK_COLORS.TEXT_PRIMARY,
backgroundColor: DARK_COLORS.HOVER,
 },
 },
 }}
>
{SETTINGS_TABS.map((tabItem) => (
<Tab label={tabItem.label} key={tabItem.label} />
 ))}
</Tabs>

<DialogContent
sx={{
pt: 1, 
pb: 2.5, 
px: 3,
minHeight: 270,
overflowY: "visible",
backgroundColor: DARK_COLORS.SURFACE,
 }}
>
{/* Profile tab */}
{tab === 1 && (
<Box>
<Box sx={{
display: 'flex',
alignItems: 'center',
gap: 2,
mb: 2,
p: 2,
borderRadius: 2,
backgroundColor: DARK_COLORS.ACCENT,
border: `1px solid ${DARK_COLORS.BORDER}`,
 }}>
<Avatar
sx={{
bgcolor: DARK_COLORS.PRIMARY,
border: `2px solid ${DARK_COLORS.TEXT_PRIMARY}`,
width: 52,
height: 52,
fontSize: 23,
fontWeight: 700,
color: DARK_COLORS.TEXT_PRIMARY,
boxShadow: "0 4px 12px rgba(0, 0, 0, 0.6)",
 }}
>
{initials}
</Avatar>
<Box>
<Typography sx={{ 
  fontWeight: 700, 
  fontSize: 20, 
  mb: 0.1,
  color: DARK_COLORS.TEXT_PRIMARY,
}}>
{fullName || "User"}
</Typography>
<Typography sx={{ 
  fontSize: 14, 
  color: DARK_COLORS.TEXT_SECONDARY,
}}>
 @{username}
</Typography>
</Box>
</Box>

<Divider sx={{ 
  mb: 2, 
  borderColor: DARK_COLORS.BORDER,
}} />

<Box sx={{ mb: 1.5 }}>
<Typography sx={{ 
  fontSize: 15, 
  color: DARK_COLORS.TEXT_SECONDARY, 
  mb: 0.5 
}}>
  {t.email}
</Typography>
<Typography sx={{ 
  fontSize: 16,
  color: DARK_COLORS.TEXT_PRIMARY,
  backgroundColor: DARK_COLORS.ACCENT,
  p: 1,
  borderRadius: 1,
  border: `1px solid ${DARK_COLORS.BORDER}`,
}}>
  {email}
</Typography>
</Box>

<Divider sx={{ 
  my: 2,
  borderColor: DARK_COLORS.BORDER,
}} />

<Button
variant="outlined"
fullWidth
onClick={onOpenChangePw}
sx={{
mb: 1.5,
textTransform: "none",
borderRadius: 2,
fontSize: 15,
color: DARK_COLORS.TEXT_PRIMARY,
borderColor: DARK_COLORS.BORDER,
backgroundColor: DARK_COLORS.ACCENT,
"&:hover": {
  backgroundColor: DARK_COLORS.HOVER,
  borderColor: DARK_COLORS.TEXT_SECONDARY,
  transform: "translateY(-1px)",
},
transition: "all 0.2s ease",
 }}
>
 {t.changePassword}
</Button>

<Button
variant="outlined"
fullWidth
onClick={onLogout}
sx={{
mb: 1,
textTransform: "none",
borderRadius: 2,
fontSize: 15,
color: DARK_COLORS.ERROR,
borderColor: DARK_COLORS.ERROR,
backgroundColor: "rgba(255, 68, 68, 0.1)",
"&:hover": {
  backgroundColor: "rgba(255, 68, 68, 0.2)",
  borderColor: DARK_COLORS.ERROR,
  transform: "translateY(-1px)",
},
transition: "all 0.2s ease",
 }}
>
 {t.logout}
</Button>

<Button
variant="text"
fullWidth
sx={{
textTransform: "none",
borderRadius: 2,
fontSize: 14,
fontWeight: 400,
color: DARK_COLORS.ERROR,
"&:hover": {
  backgroundColor: "rgba(255, 68, 68, 0.1)",
  color: DARK_COLORS.ERROR,
},
transition: "all 0.2s ease",
 }}
>
 {t.deleteAccount}
</Button>
</Box>
 )}

{/* General tab */}
{tab === 0 && (
<Box>
<Typography sx={{ 
  fontSize: 16, 
  fontWeight: 600, 
  mb: 2, 
  color: DARK_COLORS.TEXT_PRIMARY,
}}>
  {t.generalSettings}
</Typography>

<Box sx={{
display: "flex", 
alignItems: "center", 
justifyContent: "space-between",
py: 1.5, 
px: 2,
borderRadius: 2,
backgroundColor: DARK_COLORS.ACCENT,
border: `1px solid ${DARK_COLORS.BORDER}`,
mb: 1,
 }}>
<Box>
<Typography sx={{ 
  fontSize: 15, 
  fontWeight: 500,
  color: DARK_COLORS.TEXT_PRIMARY,
}}>
  {t.enableNotifications}
</Typography>
<Typography sx={{ 
  fontSize: 13.5, 
  color: DARK_COLORS.TEXT_SECONDARY,
}}>
 {t.allowNotifications}
</Typography>
</Box>
<Switch 
  sx={{
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: DARK_COLORS.TEXT_PRIMARY,
      "& + .MuiSwitch-track": {
        backgroundColor: DARK_COLORS.SUCCESS,
      },
    },
    "& .MuiSwitch-track": {
      backgroundColor: DARK_COLORS.BORDER,
    },
  }}
/>
</Box>

<Box sx={{
  display: "flex", 
  alignItems: "center", 
  justifyContent: "space-between",
  py: 1.5, 
  px: 2,
  borderRadius: 2,
  backgroundColor: DARK_COLORS.ACCENT,
  border: `1px solid ${DARK_COLORS.BORDER}`,
  mb: 1,
}}>
  <Box>
    <Typography sx={{ 
      fontSize: 15, 
      fontWeight: 500,
      color: DARK_COLORS.TEXT_PRIMARY,
    }}>
      {t.language}
    </Typography>
    <Typography sx={{ 
      fontSize: 13.5, 
      color: DARK_COLORS.TEXT_SECONDARY,
    }}>
      {t.selectLanguage}
    </Typography>
  </Box>
  <FormControl variant="standard" sx={{ minWidth: 120 }}>
    <Select
      value={language}
      onChange={(e) => handleLanguageChange(e.target.value as string)}
      sx={{
        color: DARK_COLORS.TEXT_PRIMARY,
        '.MuiSelect-icon': { color: DARK_COLORS.TEXT_PRIMARY },
        '&:before': { borderBottomColor: DARK_COLORS.BORDER },
        '&:after': { borderBottomColor: DARK_COLORS.TEXT_PRIMARY },
      }}
    >
      <MenuItem value="Italian">Italiano</MenuItem>
      <MenuItem value="French">Français</MenuItem>
    </Select>
  </FormControl>
</Box>

<Box sx={{
display: "flex", 
alignItems: "center", 
justifyContent: "space-between",
py: 1.5, 
px: 2,
borderRadius: 2,
backgroundColor: DARK_COLORS.ACCENT,
border: `1px solid ${DARK_COLORS.BORDER}`,
mb: 1,
 }}>
<Box>
<Typography sx={{ 
  fontSize: 15, 
  fontWeight: 500,
  color: DARK_COLORS.TEXT_PRIMARY,
}}>
  {t.darkMode}
</Typography>
<Typography sx={{ 
  fontSize: 13.5, 
  color: DARK_COLORS.TEXT_SECONDARY,
}}>
 {t.useDarkTheme}
</Typography>
</Box>
<Switch 
  defaultChecked
  sx={{
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: DARK_COLORS.TEXT_PRIMARY,
      "& + .MuiSwitch-track": {
        backgroundColor: DARK_COLORS.SUCCESS,
      },
    },
    "& .MuiSwitch-track": {
      backgroundColor: DARK_COLORS.BORDER,
    },
  }}
/>
</Box>
</Box>
 )}

{/* About tab */}
{tab === 2 && (
<Box>
<Typography sx={{ 
  fontWeight: 700, 
  fontSize: 18, 
  mb: 1.5, 
  color: DARK_COLORS.TEXT_PRIMARY,
}}>
  ℹ️ {t.about}
</Typography>

<Box sx={{
  backgroundColor: DARK_COLORS.ACCENT,
  border: `1px solid ${DARK_COLORS.BORDER}`,
  borderRadius: 2,
  p: 2,
  mb: 2,
}}>
<Typography sx={{ 
  color: DARK_COLORS.TEXT_SECONDARY, 
  fontSize: 14, 
  mb: 2,
  lineHeight: 1.6,
}}>
 <Box component="span" sx={{ color: DARK_COLORS.TEXT_PRIMARY, fontWeight: 600 }}>
   {t.version}
 </Box> 0.0.1<br />
<br />
 {t.contactSupport} <Box component="span" sx={{ color: DARK_COLORS.TEXT_PRIMARY, fontWeight: 600 }}>info@tecnovation.com</Box>
</Typography>
</Box>
</Box>
 )}
</DialogContent>
</Box>
</Dialog>
 );
}